import * as yup from 'yup'
import {
  pageNum,
  pageSize,
  requiredString,
  requiredNumber,
  requiredBool,
  string,
  state,
  states
} from '../yup/schemas.js'

import {
  VALIDATION,
  JOBS_BOARD_PRIVACY_TYPES,
  FILE_TYPES,
  JOBS_BOARD_TYPES,
  RECEIVE_NOTIFICATION_TYPE
} from '../constants.js'

import { jobSchema } from '../job/general.schema.js'
import { companySchema } from '../company/general.schema.js'
import { representativeSchema } from '../representative/schema.js'

const { MESSAGES, REGEXP } = VALIDATION

export const schoolSchema = yup.object({
  id: requiredString.uuid(),
  name: requiredString.min(5, MESSAGES.MIN_LENGTH(5)),
  url: requiredString.addProtocol().url(MESSAGES.URL),
  phone: requiredString.phone(),
  email: requiredString.email().toLowerCase(),
  zip: requiredString.zip(),
  street: requiredString,
  city: requiredString,
  state: state.required(),
  bannerColor: string.addHashtag().color(),
  primaryTextColor: string.addHashtag().color(),
  subdomain: string.subdomain(),
  claimed: requiredBool,
  jobAutoApprove: requiredBool,
  jobsBoardEntryPrivacyType: requiredString.oneOf(Object.values(JOBS_BOARD_PRIVACY_TYPES)),
  jobsBoardEntryToken: requiredString.simplePassword().existsWhen('jobsBoardEntryPrivacyType', value => value !== JOBS_BOARD_PRIVACY_TYPES.OPEN),
  jobsBoardExperiencedPrivacyType: requiredString.oneOf(Object.values(JOBS_BOARD_PRIVACY_TYPES)),
  jobsBoardExperiencedToken: requiredString.simplePassword().existsWhen('jobsBoardExperiencedPrivacyType', value => value !== JOBS_BOARD_PRIVACY_TYPES.OPEN),
  claimRequestSentAt: yup.date(),
  signUpRequestSentAt: yup.date()
}).objectRequired()

const createUnclaimedSchoolSchema = schoolSchema
  .pick(['name', 'url', 'phone', 'email', 'zip', 'street', 'city', 'state', 'bannerColor', 'primaryTextColor'])

const createClaimedSchoolBack = yup.object({
  school: schoolSchema.pick(['name', 'url', 'phone', 'email', 'zip', 'street', 'city', 'state']),
  representative: representativeSchema.pick(['name', 'email', 'password']),
  codeToUseOnActivation: yup.string().optional()
}).objectRequired()

const createClaimedSchoolFront = [
  createClaimedSchoolBack.pick(['representative']),
  createClaimedSchoolBack.pickNested([
    { school: ['name', 'url', 'phone', 'email'] }
  ]),
  createClaimedSchoolBack.pickNested([
    { school: ['zip', 'street', 'city', 'state'] }
  ])
]

const createClaimedRepresentativeSchoolBack = yup.object({
  school: createClaimedSchoolBack.get(['school']),
  codeToUseOnActivation: yup.string().optional()
})

const createClaimedRepresentativeSchoolFront = [
  createClaimedSchoolBack.pickNested(['name', 'url', 'phone', 'email']),
  createClaimedSchoolBack.pickNested(['zip', 'street', 'city', 'state'])
]

const schoolsClaimIdsSchema = yup.array().of(schoolSchema.get('id')).notEmpty().max(20).unique().required()
const claimSchoolsSchema = yup.object({
  schoolsIds: schoolsClaimIdsSchema,
  codeToUseOnActivation: yup.string().optional()
}).objectRequired()

const claimSchoolsAndCreateRepresentativeSchema = yup.object({
  schoolsIds: schoolsClaimIdsSchema,
  representative: representativeSchema.pick(['name', 'email', 'password']),
  codeToUseOnActivation: yup.string().optional()
}).objectRequired()

const importSchoolsSchema = yup.object({
  fields: yup.object({}).objectRequired(),
  files: yup.object({
    schools: yup.object({
      type: yup.mixed().oneOf(FILE_TYPES.CSV).required(),
      size: requiredNumber,
      path: requiredString,
      name: requiredString
    }).required()
  }).objectRequired()
}).objectRequired()

const uploadAvatarSchema = yup.object({
  fields: yup.object({}).objectRequired(),
  files: yup.object({
    avatar: yup.object({
      type: requiredString.matches(REGEXP.IMAGE_MIME, MESSAGES.IMAGE_MIME),
      size: requiredNumber.max(VALIDATION.AVATAR_MAX_SIZE),
      path: requiredString,
      name: requiredString
    }).required()
  }).objectRequired()
}).objectRequired()

const getSchoolListQueryParamsSchema = yup.object({
  pageNum,
  pageSize,
  zipCodeBy100Miles: requiredBool.optional(),
  zipCodeBy200Miles: requiredBool.optional(),
  zipCode: schoolSchema.get('zip').optional(),
  schoolNameSearch: string,
  schoolName: schoolSchema.get('name').optional(),
  urlSearch: string,
  url: string.removeProtocol(),
  phone: schoolSchema.get('phone').optional(),
  phoneSearch: string,
  email: schoolSchema.get('email').optional(),
  emailSearch: string,
  street: schoolSchema.get('street').optional(),
  city: schoolSchema.get('city').optional(),
  citySearch: string,

  state: schoolSchema.get('state').optional(),
  states: states
    .existsUnless('state', { defined: false })
    .optional(),

  claimed: schoolSchema.get('claimed').optional(),
  claimRequestSentAt: yup.boolean().isFalse().optional(),
  signUpRequestSentAt: yup.boolean().isFalse().optional()
}).objectRequired()

const subsribeToSchoolBoardSchema = yup.object({
  email: requiredString.email().toLowerCase(),
  name: requiredString,
  boardType: requiredString.oneOf(Object.values(JOBS_BOARD_TYPES)),
  receiveNotificationType: requiredString.oneOf(Object.values(RECEIVE_NOTIFICATION_TYPE))
}).objectRequired()

const getSchoolSchema = schoolSchema.pick(['id'])
const getSchoolBySubdomainSchema = schoolSchema.pick(['subdomain'])

const patchSchoolSchemaBack = schoolSchema
  .pick([
    'name', 'url', 'phone', 'email', 'zip',
    'street', 'city', 'state', 'bannerColor', 'primaryTextColor', 'jobAutoApprove',
    'jobsBoardEntryPrivacyType', 'jobsBoardEntryToken',
    'jobsBoardExperiencedPrivacyType', 'jobsBoardExperiencedToken',
    'subdomain'
  ])
  .allFieldsOptional()

const patchSchoolSchemaFront = schoolSchema
  .pick(['name', 'url', 'phone', 'email', 'zip', 'street', 'city', 'state'])

const schoolSettingsVisibilitySchema = schoolSchema
  .pick([
    'jobsBoardEntryPrivacyType', 'jobsBoardExperiencedPrivacyType', 'jobsBoardEntryToken', 'jobsBoardExperiencedToken'
  ])

const getSchoolJobParamsSchema = yup.object({
  id: schoolSchema.get('id'),
  jobId: jobSchema.get('id')
}).objectRequired()

const getSchoolCompanyParamsSchema = yup.object({
  id: schoolSchema.get('id'),
  companyId: companySchema.get('id')
}).objectRequired()

const bulkCreateUnclaimedSchoolSchemas = {
  front: yup.array().of(createUnclaimedSchoolSchema).required(),
  // TODO: Add noUnknown
  back: yup.array().of(createUnclaimedSchoolSchema.noUnknown(false)).required()
}

const importSchoolsSchemas = {
  front: yup.object({
    schools: importSchoolsSchema.get('files.schools.path')
  }).objectRequired(),
  back: importSchoolsSchema
}

const uploadAvatarSchemas = {
  front: yup.object({
    avatar: uploadAvatarSchema.get('files.avatar.path')
  }).objectRequired(),
  back: uploadAvatarSchema
}

const increaseUnclaimedSchoolsShownTimesSchemas = {
  front: yup.array().of(schoolSchema.get('id')).notEmpty().required(),
  back: yup.array().of(schoolSchema.get('id')).notEmpty().required()
}

const subsribeToSchoolBoardSchemas = {
  front: subsribeToSchoolBoardSchema,
  back: subsribeToSchoolBoardSchema
}

const getSchoolSchemas = { back: getSchoolSchema }
const getSchoolBySubdomainSchemas = { back: getSchoolBySubdomainSchema }
const getSchoolListQueryParamsSchemas = { front: getSchoolListQueryParamsSchema, back: getSchoolListQueryParamsSchema }

const getSchoolsByNameSchemas = { front: schoolSchema.pick(['name']).allFieldsOptional() }
const getSchoolJobParamsSchemas = { front: getSchoolJobParamsSchema, back: getSchoolJobParamsSchema }
const getSchoolCompanyParamsSchemas = { front: getSchoolCompanyParamsSchema, back: getSchoolCompanyParamsSchema }

const createClaimedSchoolSchemas = { front: createClaimedSchoolFront, back: createClaimedSchoolBack }
const createClaimedRepresentativeSchoolSchemas = { front: createClaimedRepresentativeSchoolFront, back: createClaimedRepresentativeSchoolBack }
const claimSchoolsSchemas = { front: claimSchoolsSchema, back: claimSchoolsSchema }
const claimSchoolsAndCreateRepresentativeSchemas = { front: claimSchoolsAndCreateRepresentativeSchema, back: claimSchoolsAndCreateRepresentativeSchema }
const patchSchoolSchemas = { front: patchSchoolSchemaFront, back: patchSchoolSchemaBack }

export default {
  createClaimedSchool: createClaimedSchoolSchemas,
  createClaimedRepresentativeSchool: createClaimedRepresentativeSchoolSchemas,
  bulkCreateUnclaimedSchool: bulkCreateUnclaimedSchoolSchemas,
  importSchools: importSchoolsSchemas,
  uploadAvatar: uploadAvatarSchemas,
  claimSchools: claimSchoolsSchemas,
  claimSchoolsAndCreateRepresentative: claimSchoolsAndCreateRepresentativeSchemas,
  getSchoolListQueryParams: getSchoolListQueryParamsSchemas,
  getSchoolParams: getSchoolSchemas,
  getSchoolBySubdomainParams: getSchoolBySubdomainSchemas,
  getSchoolsByName: getSchoolsByNameSchemas,
  increaseUnclaimedSchoolsShownTimes: increaseUnclaimedSchoolsShownTimesSchemas,
  getSchoolJobParams: getSchoolJobParamsSchemas,
  getSchoolCompanyParams: getSchoolCompanyParamsSchemas,
  patchSchool: patchSchoolSchemas,
  schoolSettingsVisibility: schoolSettingsVisibilitySchema,
  subsribeToSchoolBoard: subsribeToSchoolBoardSchemas
}
